// ──────────────────────────────────────────────────────
// ProductCardLegacy — extracted pre-Stage-B inline card (80x80 image,
// name/brand/price/shortReasonAr). Used as the safety net when
// NEXT_PUBLIC_PREMIUM_CARDS !== '1' or when product.role is absent/default,
// AND as the canonical renderer for routine + compare + legacy_multi layouts
// (see CardRenderer's layout override).
//
// Phase-2 trust injection (2026-04-21): a small header strip with the
// compact TrustBadge sits above the original image+text row. Adding a
// wrapper row is the minimum intrusion that preserves the original 80×80
// image sizing + existing text column — visual parity of the inner layout
// is intact; only a thin authenticity header is added above it.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBadge } from '@/components/TrustBadge';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

export function ProductCardLegacy({ product }: { product: ProductSuggestion }) {
    const ps = product;
    const outOfStock = isProductOutOfStock(ps);
    return (
        <Link
            href={ps.href}
            className="block rounded-xl overflow-hidden transition-shadow hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
            style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                textDecoration: 'none',
                direction: 'rtl',
            }}
        >
            {/* Trust header — authenticity badge visible on every legacy
                card (routine / compare / fallback). Kept minimal: single
                row, small compact badge, right-aligned in RTL. */}
            <div className="flex items-center justify-between gap-2 px-2.5 pt-2 pb-1">
                {outOfStock ? (
                    <span
                        className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold"
                        style={{
                            background: '#fef3f2',
                            color: '#b42318',
                            border: '1px solid #fecdca',
                            padding: '2px 6px',
                        }}
                        aria-label={OUT_OF_STOCK_LABEL_AR}
                    >
                        {OUT_OF_STOCK_LABEL_AR}
                    </span>
                ) : <span />}
                <TrustBadge compact />
            </div>
            {/* Original image + text row — unchanged aside from the wrapping
                container switch from `flex` on the Link to `block`+inner-flex. */}
            <div className="flex">
                <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{ width: 80, height: 80, background: '#f8f9fb' }}
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={ps.imageUrl}
                        alt={ps.nameAr}
                        className="w-full h-full object-contain p-1.5 q-card-image-fade"
                        // QCARE-AI-DEV-MOBILE-CHAT-ROUTINE-BLOCK-REVEAL-PHASE-1-5
                        // (2026-05-04): images start at opacity 0 (via the
                        // `.q-card-image-fade` class) and flip to opacity 1
                        // on load. The class also defines the 380ms ease
                        // transition + reduced-motion guard.
                        onLoad={(e) => {
                            const el = e.target as HTMLImageElement;
                            if (el.dataset.fallback === '1') return;  // keep 0.55 fallback
                            el.style.opacity = '1';
                        }}
                        // External URLs that hotlink-block render as a
                        // broken icon by default. Swap to the QIO logo
                        // placeholder so the card stays visually clean.
                        onError={(e) => {
                            const el = e.target as HTMLImageElement;
                            if (el.dataset.fallback === '1') return;
                            el.dataset.fallback = '1';
                            el.src = '/branding/qio-logo.jpeg';
                            el.style.opacity = '0.55';
                        }}
                    />
                </div>
                <div className="flex-1 p-2.5 flex flex-col justify-center min-w-0" style={{ borderRight: '1px solid #f0f0f0' }}>
                    {/* QCARE-AI-DEV-MOBILE-CHAT-INPUT-AUTOGROW-AND-PRODUCT-TITLE-SIZE
                        (2026-05-04): legacy product-card name bumped from
                        text-xs (12px) to text-sm (14px). Two-line clamp +
                        semibold preserved. */}
                    <div
                        className="text-sm font-semibold leading-tight mb-0.5"
                        style={{ color: '#1a1a2e', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                        {ps.nameAr}
                    </div>
                    {ps.brandAr && (
                        <div className="text-[10px] mb-0.5" style={{ color: '#94a3b8' }}>
                            {ps.brandAr}
                        </div>
                    )}
                    {ps.price != null && ps.price > 0 && (
                        <div className="text-base font-bold mb-0.5" style={{ color: '#0d8f8f' }}>
                            {ps.price.toFixed(2)} ريال
                        </div>
                    )}
                    {ps.shortReasonAr && (
                        <div
                            className="text-[10px] leading-snug"
                            style={{ color: '#0d8f8f', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                        >
                            {ps.shortReasonAr}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}
